'use client';
import { BoxComponent, CardComponent, SnackbarComponent, TypographyComponent } from '@zeus/web-components';
import React, { useEffect, useMemo, useState } from 'react';
import { styles } from './cartItem.styles';
import Image from 'next/image';
import plusIcon from '../../resources/assets/plus.svg';
import minusIcon from '../../resources/assets/minus.svg';
import { CartReducerActionType, Order } from '../(routes)/online-ordering/types';
import { IconButton, useMediaQuery } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  checkTimeBasedAvailability,
  createDefaultArray,
  createFilteredModifiers,
  getImage,
  handleGetAppliedDeals,
  sendEventToPostHog,
  setUserAttributeForQR,
} from '../../utils/UtilFunctions';
import { useRouter } from 'next/navigation';
import { ModifierGroup, StockStatus } from '@zeus/shared';
import { setUserAttribute } from '../../utils/UtilFunctions';
import { sendGAEvent } from '@next/third-parties/google';
import { EventAction, EventType, QREventActions } from '../../config/GAConstants';
import WarningBar from './Warningbar';
import pizza_full_inactive from './../../public/assets/pizza_full_inactive.png';
import pizza_left_inactive from './../../public/assets/pizza_left_inactive.png';
import pizza_right_inactive from './../../public/assets/pizza_right_inactive.png';
import { setCookie } from 'cookies-next';
import CartStatusNotifier from '../(routes)/online-ordering/[resturants]/cart/_component/CartStatusNotifier';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setCategory } from '@store/online/menu/menuSlice';
import { updateCartSettings } from '@store/online/cart/cartSlice';
import theme from '../../theme/Theme';
import { useActiveCart, useCartActions } from '@store/online/cart/cartHooks';
import { CheckoutError } from '@store/online/cart/cartTypes';

const CartItem = ({
  item: cartItem,
  itemIndex,
  setItemDeleted,
  isItemUnavailable,
  validationLoading,
  checkoutError,
}: {
  item: Order | any;
  itemIndex: number;
  setItemDeleted?: any;
  isItemUnavailable?: boolean;
  validationLoading?: boolean;
  checkoutError?: CheckoutError;
}) => {
  const getOptionLabel = (optionDisplayName?: string, optionName?: string) => {
    if (optionDisplayName && optionDisplayName.trim().toLowerCase() !== 'null') {
      return optionDisplayName;
    }
    return optionName || '';
  };

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { cart } = useActiveCart();
  const cartActions = useCartActions();
  // const [modOutStock, setModOutStock] = useState(false);
  const { selectedModifierGroups, orderedQuantity, id, lowOnStock, isDiscount } = cartItem;
  const remainingQuantity = cartItem?.quantity - orderedQuantity;
  const [leftSelectedModifierGroups, setLeftSelectedModifierGroups] = useState<ModifierGroup[]>([]);
  const [rightSelectedModifierGroups, setRightSelectedModifierGroups] = useState<ModifierGroup[]>([]);
  const [fullSelectedModifierGroups, setFullSelectedModifierGroups] = useState<ModifierGroup[]>([]);
  const [defaultModifierGroups, setDefaultModifierGroups] = useState<ModifierGroup[]>([]);

  const [quantity, setQuantity] = useState(cartItem?.orderedQuantity);
  const router = useRouter();
  const [snackbar, setSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('Max limit reached');
  const [isDealRemoved, setIsDealRemoved] = useState(false);
  const [dealsToBeRemoved, setDealsToBeRemoved] = useState<any[]>([]);
  const [actionType, setActionType] = useState<CartReducerActionType>();
  //store states
  const isQr = useSelector((state: RootState) => state.restaurant.isQr);
  const visibility = useSelector((state: RootState) => state?.settings?.visibilitySettings['Item']);
  const tenantName = useSelector((state: RootState) => state.restaurant.tenantName);

  const dispatch = useDispatch();

  const totalModifierPrice = useMemo(() => {
    if (cartItem.isLoyaltyApplied) {
      return selectedModifierGroups.reduce((total: number, group: ModifierGroup) => {
        const groupTotal = (group.modifiers || []).reduce((modSum, modifier) => {
          if (modifier.modifierOption?.length) {
            const optionTotal = modifier.modifierOption.reduce(
              (optSum, option) => optSum + (Number(option.price) || 0) * (option.orderedQuantity ?? 1),
              0
            );
            return modSum + optionTotal;
          }

          if (modifier.nestedModifiers?.length) {
            const nestedTotal = modifier.nestedModifiers.reduce((nestedSum, nestedModifier) => {
              const nestedOptionTotal =
                nestedModifier.modifierOption?.reduce(
                  (optSum, option) => optSum + (Number(option.price) || 0) * (option.orderedQuantity ?? 1),
                  0
                ) || 0;

              return nestedSum + nestedOptionTotal;
            }, 0);

            return modSum + nestedTotal;
          }

          return modSum;
        }, 0);

        return total + groupTotal;
      }, 0);
    }
    return null;
  }, [selectedModifierGroups]);

  const createCartPlusProperties = (
    cartItem: { id?: string; itemName?: string; itemPrice?: number } | null,
    user: Record<string, any>
  ) => {
    return {
      item_id: cartItem?.id || null,
      item_name: cartItem?.itemName || null,
      item_price: cartItem?.itemPrice?.toFixed(2) || null,
      ...user,
    };
  };

  const handlePlus = async (newQuantity: number) => {
    if (cartItem?.isLoyaltyApplied) {
      setSnackbar(true);
      setSnackbarMessage('You can redeem only 1 item per order');
      return;
    }
    const maxLimit = cartItem?.maxLimit ?? 1;
    if (!cartItem.noMaxLimit && cartItem?.orderedQuantity == maxLimit) {
      setSnackbarMessage('Max limit reached');
      setSnackbar(true);

      return;
    }
    cartActions.incrementItemQuantity(cartItem, itemIndex);
    // For Google Analytics
    const eventAction = isQr ? QREventActions.QR_CART_PLUS : EventAction.OL_CART_PLUS;
    const user = isQr ? setUserAttributeForQR() : setUserAttribute();
    const cartPlusProperties = createCartPlusProperties(cartItem, user);
    sendGAEvent(EventType.EVENT, eventAction, cartPlusProperties);
    sendEventToPostHog(EventType.EVENT, eventAction, cartPlusProperties);
  };

  const handleMinus = async (newQuantity: number) => {
    if (quantity >= 1) {
      const newPrice = await cartActions.checkDecrementPricing(cartItem.id, cartItem.selectedModifierGroups);

      let minCriteriaDeals = handleGetAppliedDeals(cart);
      minCriteriaDeals = minCriteriaDeals.filter((dealData: any) => {
        return dealData?.deal?.details?.isMinimumSpend && dealData?.deal?.details?.minimumSpend > newPrice;
      });

      if (minCriteriaDeals.length > 0) {
        setIsDealRemoved(true);
        setDealsToBeRemoved(minCriteriaDeals);
        setActionType(CartReducerActionType.DecrementInCart);
      } else {
        setQuantity(newQuantity);
        cartActions.decrementItemQuantity(cartItem, itemIndex);
      }
      const eventAction = isQr ? QREventActions.QR_CART_MINUS : 'ol_cart_minus';
      const user = isQr ? setUserAttributeForQR() : setUserAttribute();
      const cartPlusProperties = createCartPlusProperties(cartItem, user);
      sendGAEvent('event', eventAction, cartPlusProperties);
      sendEventToPostHog('event', eventAction, cartPlusProperties);
    }
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // actions.toggleCartOption({ isCartLoading: true });
    e.stopPropagation();
    e.preventDefault();
    const newPrice = await cartActions.checkDeletePricing(cartItem.id, itemIndex);

    let minCriteriaDeals = handleGetAppliedDeals(cart);
    minCriteriaDeals = minCriteriaDeals.filter((dealData: any) => {
      return dealData?.deal?.details?.isMinimumSpend && dealData?.deal?.details?.minimumSpend > newPrice;
    });
    if (minCriteriaDeals.length > 0) {
      setIsDealRemoved(true);
      setDealsToBeRemoved(minCriteriaDeals);
      setActionType(CartReducerActionType.DeleteItem);
    } else {
      cartActions.deleteItem(cartItem, itemIndex);
    }
    if (setItemDeleted) {
      setItemDeleted(true);
      // setModOutStock(false);
    }
    const eventAction = isQr ? QREventActions.QR_CART_DELETE : EventAction.OL_CART_DELETE;
    const user = isQr ? setUserAttributeForQR() : setUserAttribute();
    const cartPlusProperties = createCartPlusProperties(cartItem, user);
    sendGAEvent(EventType.EVENT, eventAction, cartPlusProperties);
    sendEventToPostHog(EventType.EVENT, eventAction, cartPlusProperties);
    if (isQr) {
      if (cartItem?.settingId && !checkTimeBasedAvailability(visibility?.[cartItem?.settingId])) {
        //if (!cartItem.isSettingsVisible) {
        sendGAEvent(EventType.EVENT, QREventActions.QR_CART_REMOVEITEMUNAVAILABLE);
        sendEventToPostHog(EventType.EVENT, QREventActions.QR_CART_REMOVEITEMUNAVAILABLE);
      }
      if (cartItem.stockStatus === StockStatus.OutOfStock || cartItem.stockStatus === StockStatus.False) {
        sendGAEvent(EventType.EVENT, QREventActions.QR_CART_REMOVEITEMOUTOFSTOCK);
        sendEventToPostHog(EventType.EVENT, QREventActions.QR_CART_REMOVEITEMOUTOFSTOCK);
      }
    }
  };
  const handleEditItem = async (e: any) => {
    let path = `/online-ordering/${tenantName}/${cartItem.id}`;
    if ((e.target as HTMLElement).closest('.buttonClass')) {
      return;
    }
    dispatch(setCategory({ categoryId: cartItem.categoryId }));
    dispatch(updateCartSettings({ cartItem }));

    if (cartItem.isLoyaltyApplied) {
      setCookie('isRedeemed', true);
    }

    const queryParams = [
      isQr && 'qr=true',
      cartItem.isLoyaltyApplied && 'isItemRedeemed=true',
      cartItem.categoryId && 'category=' + cartItem.categoryId,
      cartItem.menuId && 'menu=' + cartItem.menuId,
    ]
      .filter(Boolean)
      .join('&');

    path = `${path}${queryParams ? `?${queryParams}` : ''}`;

    if (cartItem && id) {
      router.push(path);
    }
  };

  // const saveAndSync = async (newState: CartActionTypes) => {
  //   await actions.saveCart(newState);
  //   await actions.syncCart(newState);
  //   await actions.toggleCartOption({ isCartLoading: false });
  // };

  useEffect(() => {
    setQuantity(cartItem?.orderedQuantity);
  }, [cartItem?.orderedQuantity]);

  // const hasModifierOutOfStock = useMemo(() => {
  //   let hasOutOfStock = false;

  //   selectedModifierGroups.forEach((group: ModifierGroup) => {
  //     group.modifiers?.forEach((modifier) => {
  //       if (modifier.stockStatus === false) {
  //         hasOutOfStock = true;
  //       }
  //       modifier.modifierOption?.forEach((modOption) => {
  //         if (modOption.isStockAvailable === false) {
  //           hasOutOfStock = true;
  //         }
  //       });
  //       if (modifier.nestedModifiers?.length) {
  //         modifier.nestedModifiers?.forEach((nestedMod) => {
  //           if (nestedMod.stockStatus === false) {
  //             hasOutOfStock = true;
  //           }
  //           nestedMod.modifierOption?.forEach((modOption) => {
  //             if (modOption.isStockAvailable === false) {
  //               hasOutOfStock = true;
  //             }
  //           });
  //         });
  //       }
  //     });
  //   });

  //   return hasOutOfStock;
  // }, [selectedModifierGroups]);

  useEffect(() => {
    // setModOutStock(hasModifierOutOfStock);

    // if (cart && hasModifierOutOfStock !== cart.modifierOutStock) {
    //   // cartActions.setState({ modifierOutStock: hasModifierOutOfStock });
    // }

    setLeftSelectedModifierGroups(createFilteredModifiers(selectedModifierGroups, 'left'));
    setRightSelectedModifierGroups(createFilteredModifiers(selectedModifierGroups, 'right'));
    setFullSelectedModifierGroups(createFilteredModifiers(selectedModifierGroups, 'full'));
    setDefaultModifierGroups(createDefaultArray(selectedModifierGroups));
  }, [
    selectedModifierGroups,
    //  hasModifierOutOfStock,
    cart?.modifierOutStock,
    cartActions,
  ]);

  const renderModifiersGroup = (selectedModifier: ModifierGroup) => {
    return (
      console.log('selectedModifier', selectedModifier),
      (
        <>
          <TypographyComponent
            variant="body1"
            sx={{ color: '#242528', fontSize: { xs: '10px', md: '12px' }, fontWeight: 500, wordWrap: 'break-word' }}
          >
            {selectedModifier.modifierGroupName}
          </TypographyComponent>

          {selectedModifier?.modifiers && selectedModifier?.modifiers?.length > 0 ? (
            selectedModifier?.modifiers?.map((mod) => (
              <>
                {mod?.modifierOption && mod?.modifierOption.length > 0 ? (
                  <BoxComponent sx={styles.modItems}>
                    {/* {selectedModifier.modifierGroupName !== mod.modifierName ? (
                    <TypographyComponent
                      variant="caption"
                      sx={{ color: mod.stockStatus ? '#888D95' : '#E24545', fontWeight: 400 }}
                    >
                      {mod.modifierName}
                    </TypographyComponent>
                  ) : (
                    <></>
                  )} */}
                    {mod?.modifierOption?.map((options) => (
                      <>
                        <BoxComponent key={options.id} sx={styles.modItems}>
                          <TypographyComponent
                            variant="caption"
                            sx={{
                              color: options.isStockAvailable ? '#888D95' : 'warning.main',
                              fontWeight: 500,
                              fontSize: { xs: '12px', md: '14px' },
                            }}
                          >
                            {options.orderedQuantity ? <>{options.orderedQuantity}x </> : <></>}
                            {getOptionLabel(options?.optionDisplayName, options?.optionName)}
                            {options.price && options.price > 0 ? <>+${options.price.toFixed(2)}</> : <></>}
                          </TypographyComponent>
                        </BoxComponent>
                      </>
                    ))}
                  </BoxComponent>
                ) : (
                  <></>
                )}
                {mod?.nestedModifiers && mod?.nestedModifiers.length > 0 ? (
                  mod?.nestedModifiers?.map((modifier) => (
                    <BoxComponent key={modifier.id}>
                      {/* <TypographyComponent
                      variant="caption"
                      sx={{ color: modifier.stockStatus ? '#888D95' : '#E24545', fontWeight: 400 }}
                    >
                      {modifier.orderedQuantity && modifier?.orderedQuantity > 1 && <>{modifier.orderedQuantity}x </>}
                      {modifier.modifierName}
                    </TypographyComponent> */}
                      {modifier && modifier?.modifierOption && modifier?.modifierOption?.length > 0 && (
                        <BoxComponent sx={styles.modItems}>
                          {modifier?.modifierOption?.map((options) => (
                            <>
                              <BoxComponent key={options.id} sx={styles.modItems}>
                                <TypographyComponent
                                  variant="caption"
                                  sx={{
                                    color: options?.isStockAvailable ? '#888D95' : 'warning.main',
                                    fontWeight: 400,
                                    ml: '5px',
                                  }}
                                >
                                  {getOptionLabel(options?.optionDisplayName, options?.optionName)}
                                  {options.price && options.price > 0 ? <>+${options.price.toFixed(2)}</> : <></>}
                                </TypographyComponent>
                              </BoxComponent>
                            </>
                          ))}
                        </BoxComponent>
                      )}
                    </BoxComponent>
                  ))
                ) : (
                  <></>
                )}
              </>
            ))
          ) : (
            <></>
          )}
        </>
      )
    );
  };

  return (
    <CardComponent
      id="cart-item"
      sx={lowOnStock ? styles.lowStockCard : styles.card}
      style={{ marginBottom: 10, overflow: 'auto' }}
    >
      {lowOnStock && (
        <BoxComponent sx={styles.topContainer}>
          <TypographyComponent sx={styles.topContainerLabel} id="cart-item__low-stock">
            <WarningAmberOutlinedIcon sx={{ color: 'warning.main', marginRight: '10px' }} fontSize="large" /> Low Stock
            (only {remainingQuantity} available)
          </TypographyComponent>
        </BoxComponent>
      )}
      {checkoutError && checkoutError.isValid === false && (
        <WarningBar color="warning" text={checkoutError.message} handleDelete={handleDelete} />
      )}
      <BoxComponent sx={styles.itemContainer}>
        <BoxComponent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            height: { xs: '72px', md: '90px' },
            width: { xs: '72px', md: '90px' },
          }}
          id="cart-items__edit-item-btn"
        >
          <Image
            src={getImage(cartItem?.onlineImage ? cartItem?.onlineImage : '').url}
            style={{ borderRadius: '10px', objectFit: 'contain', cursor: 'pointer' }}
            fill
            alt="cart-item"
            id="cart-items__image"
            onClick={handleEditItem}
          />
        </BoxComponent>
        <BoxComponent id="cart-items__content" sx={styles.content}>
          <BoxComponent sx={styles.contentContainer} id="cart-items__edit-item-btn2">
            <TypographyComponent
              id="cart-items__item-name"
              variant="subtitle2"
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                fontWeight: 500,
                wordWrap: 'break-word',
                cursor: 'pointer',
                display: 'inline-block',
                width: 'fit-content',
              }}
              onClick={handleEditItem}
            >
              {cartItem?.itemName ? cartItem?.itemName : ''}
            </TypographyComponent>

            {isDiscount === true ? (
              <BoxComponent id="cart-items__price-container" sx={{ display: 'flex', flexDirection: 'column' }}>
                <TypographyComponent
                  id="cart-items__actual-price"
                  sx={{ color: '#888D95', textDecoration: 'line-through', fontSize: '12px' }}
                >
                  ${cartItem?.actualPrice?.toFixed(2)}
                </TypographyComponent>
                <TypographyComponent
                  id="cart-items__discount-price"
                  data-testid={`cm-item-price-text${cartItem?.id}`}
                  variant="subtitle2"
                  sx={{ color: '#242528' }}
                >
                  ${cartItem?.discountedPrice?.toFixed(2)}
                </TypographyComponent>
              </BoxComponent>
            ) : (
              <BoxComponent
                sx={{
                  display: 'flex',
                  gap: '8px',

                  '@media (min-width: 320px) and (max-width: 360px)': {
                    flexWrap: 'wrap',
                  },
                }}
              >
                {cartItem?.isLoyaltyApplied ? (
                  <>
                    {cartItem.discountedPrice == 0 ? (
                      <TypographyComponent variant="subtitle2" sx={styles.priceLabel}>
                        Free
                      </TypographyComponent>
                    ) : (
                      <TypographyComponent
                        id="cart-items__discount-price"
                        data-testid={`cm-item-price-text${cartItem?.id}`}
                        variant="subtitle2"
                        sx={{
                          color: '#242528',
                          fontSize: { xs: '14px', md: '18px' },
                          fontWeight: { xs: 400, md: 500 },
                        }}
                      >
                        ${Number(cartItem?.discountedPrice)?.toFixed(2)}
                      </TypographyComponent>
                    )}
                    {cartItem?.discountedPrice !== Number(cartItem?.itemPrice) + Number(cartItem?.discountedPrice) && (
                      <TypographyComponent
                        variant="subtitle2"
                        sx={{
                          textDecoration: 'line-through',
                          color: '#888D95',
                          fontSize: { xs: '14px', md: '18px' },
                        }}
                      >
                        {((cartItem?.itemPrice ?? 0) + (totalModifierPrice ?? 0)).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </TypographyComponent>
                    )}
                  </>
                ) : (
                  <TypographyComponent
                    id="cart-items__discount-price"
                    data-testid={`cm-item-price-text${cartItem?.id}`}
                    variant="subtitle2"
                    sx={{ color: '#242528', fontSize: { xs: '14px', md: '16px' }, fontWeight: 500 }}
                  >
                    {(cartItem?.discountedPrice ?? 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TypographyComponent>
                )}
              </BoxComponent>
            )}
          </BoxComponent>
          {/* looping modifiers from metadata */}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <BoxComponent sx={styles.modLadt} onClick={handleEditItem} id="cart-items__edit-item-btn3">
              {leftSelectedModifierGroups && leftSelectedModifierGroups?.length > 0 && (
                <BoxComponent sx={{ display: 'flex', alignItems: 'start' }}>
                  <IconButton sx={{ mr: '5px', pt: '5px' }}>
                    <Image src={pizza_left_inactive} alt="minusIcon" width={15} height={30} />
                  </IconButton>
                  <BoxComponent sx={styles.modContainer}>
                    {leftSelectedModifierGroups.map((selectedModifier: ModifierGroup, index: number) => (
                      <BoxComponent key={selectedModifier?.id}>{renderModifiersGroup(selectedModifier)}</BoxComponent>
                    ))}
                  </BoxComponent>
                </BoxComponent>
              )}
              {rightSelectedModifierGroups && rightSelectedModifierGroups?.length > 0 && (
                <BoxComponent sx={{ display: 'flex', alignItems: 'start' }} id="cart-items__right-modifiers">
                  <IconButton sx={{ mr: '5px', pt: '5px' }}>
                    <Image src={pizza_right_inactive} alt="minusIcon" width={15} height={30} />
                  </IconButton>
                  <BoxComponent sx={styles.modContainer} id="cart-items__right-modifier-groups">
                    {rightSelectedModifierGroups.map((selectedModifier: ModifierGroup, index: number) => (
                      <BoxComponent key={selectedModifier?.id} id={`cart-items__right-modifier-item${index}`}>
                        {renderModifiersGroup(selectedModifier)}
                      </BoxComponent>
                    ))}
                  </BoxComponent>
                </BoxComponent>
              )}
              {fullSelectedModifierGroups && fullSelectedModifierGroups?.length > 0 && (
                <BoxComponent sx={{ display: 'flex', alignItems: 'start' }}>
                  <IconButton sx={{ mr: '5px', pt: '5px' }}>
                    <Image src={pizza_full_inactive} alt="minusIcon" width={30} height={30} />
                  </IconButton>
                  <BoxComponent sx={styles.modContainer} id="cart-items__full-modifier-groups">
                    {fullSelectedModifierGroups.map((selectedModifier: ModifierGroup, index: number) => (
                      <BoxComponent key={selectedModifier?.id} id={`cart-items__full-modifier-item${index}`}>
                        {renderModifiersGroup(selectedModifier)}
                      </BoxComponent>
                    ))}
                  </BoxComponent>
                </BoxComponent>
              )}

              {defaultModifierGroups && defaultModifierGroups?.length > 0 && (
                <BoxComponent sx={{ display: 'flex', alignItems: 'start' }}>
                  <BoxComponent sx={styles.modContainer} id="cart-items__default-modifier-groups">
                    {defaultModifierGroups.map((selectedModifier: ModifierGroup, index: number) => (
                      <BoxComponent key={selectedModifier?.id} id={`cart-items__default-modifier-item${index}`}>
                        {renderModifiersGroup(selectedModifier)}
                      </BoxComponent>
                    ))}
                  </BoxComponent>
                </BoxComponent>
              )}
              {cartItem?.special_instructions && (
                <BoxComponent sx={{ display: 'flex', alignItems: 'start' }}>
                  <BoxComponent sx={styles.modContainer} id="cart-items__special-instructions">
                    <TypographyComponent
                      variant="body1"
                      sx={{
                        color: '#53575D',
                        fontSize: { md: '14px' },
                        fontWeight: { xs: 600, md: 500 },
                        wordWrap: 'break-word',
                      }}
                    >
                      Special Instructions
                    </TypographyComponent>
                    <BoxComponent sx={styles.modItems}>
                      <TypographyComponent
                        variant="caption"
                        sx={{ color: '#888D95', fontWeight: 400, maxWidth: '70%', whiteSpace: 'pre-line' }}
                      >
                        {isSmallScreen ? (
                          <div className="text-sm leading-tight">
                            <span>{cartItem.special_instructions.substring(0, 25)}</span>
                            <br />
                            <span className="text-gray-600">{cartItem.special_instructions.substring(25)}</span>
                          </div>
                        ) : (
                          <span
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {cartItem.special_instructions}
                          </span>
                        )}
                      </TypographyComponent>
                    </BoxComponent>
                  </BoxComponent>
                </BoxComponent>
              )}
            </BoxComponent>
            <BoxComponent id="cart-items__action-container" sx={styles.actionContainer}>
              {quantity === 1 || (cartItem?.minLimit && cartItem.minLimit === cartItem.orderedQuantity) ? (
                <IconButton
                  id="cart-items__delete-button"
                  data-testid={`cm-item-delete-btn-${cartItem.id}`}
                  onClick={handleDelete}
                  sx={{ height: { xs: '20px', md: '23px' }, width: { xs: '20px', md: '23px' }, mr: '5px' }}
                >
                  <Image src="/assets/deleteIcon.svg" alt="deleteIcon" fill />
                </IconButton>
              ) : (
                <IconButton
                  id="cart-items__minus-button"
                  onClick={() => handleMinus(quantity - 1)}
                  disabled={quantity <= 1}
                  sx={{ height: { xs: '20px', md: '24px' }, width: { xs: '20px', md: '24px' }, mr: '5px' }}
                >
                  <Image src={minusIcon} alt="minusIcon" fill />
                </IconButton>
              )}
              <TypographyComponent variant="subtitle1" sx={{ fontWeight: 500, fontSize: '12px', color: '#ED7C69' }}>
                {quantity}
              </TypographyComponent>
              <IconButton
                id="cart-items__plus-button"
                data-testid={`cm-item-plus-btn-${cartItem.id}`}
                onClick={() => handlePlus(quantity + 1)}
                sx={{
                  padding: '8px 0px 5px 8px',
                  height: { xs: '20px', md: '24px' },
                  width: { xs: '20px', md: '24px' },
                  ml: '5px',
                  ...(cartItem?.isLoyaltyApplied && { opacity: 0.3 }),
                }}
              >
                <Image src={plusIcon} alt="plusIcon" fill />
              </IconButton>
            </BoxComponent>
          </div>
        </BoxComponent>
      </BoxComponent>

      {/* Deal - Remove Dialog */}
      {isDealRemoved && (
        <CartStatusNotifier
          setIsDealRemoved={setIsDealRemoved}
          dealsToBeRemoved={dealsToBeRemoved}
          actionType={actionType}
          quantity={quantity}
          setQuantity={setQuantity}
          item={cartItem}
          itemIndex={itemIndex}
        />
      )}

      <SnackbarComponent open={snackbar} setOpen={setSnackbar} message={snackbarMessage} style={styles.snackbar} />
    </CardComponent>
  );
};

export default CartItem;
